import { DestroyRef, Injectable, InjectionToken, NgZone, inject } from '@angular/core';
import { SessionStorageService } from '../../../../attachments-handler/src/lib/services/session-storage.service';
import { environment } from '../../environments/environment';

export const ALLOWED_PARENT_ORIGINS = new InjectionToken<readonly string[]>(
  'ALLOWED_PARENT_ORIGINS',
  {
    providedIn: 'root',
    factory: () => environment.allowedParentOrigins,
  },
);

/**
 * Supports exact origins ("https://mlsz-tao.lan") and
 * wildcard hostname suffixes ("*.flexinform") — never bare "*".
 */
function isOriginAllowed(origin: string, patterns: readonly string[]): boolean {
  return patterns.some(pattern => {
    if (pattern.startsWith('*.')) {
      try {
        return new URL(origin).hostname.endsWith(pattern.slice(1));
      } catch {
        return false;
      }
    }
    return origin === pattern;
  });
}

@Injectable({ providedIn: 'root' })
export class LogoutMessageListenerService {
  private readonly ngZone = inject(NgZone);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly allowedOrigins = inject(ALLOWED_PARENT_ORIGINS);
  private loggedOut = false;

  init(): void {
    const handler = (event: MessageEvent) => this.handleMessage(event);
    window.addEventListener('message', handler);
    this.destroyRef.onDestroy(() => window.removeEventListener('message', handler));
  }

  private handleMessage(event: MessageEvent): void {
    if (!isOriginAllowed(event.origin, this.allowedOrigins)) {
      if (!environment.production) {
        console.debug('[LogoutListener] Message from unknown origin ignored:', event.origin);
      }
      return;
    }
    if (event.data?.type !== 'logout') return;
    if (event.data?.source !== 'tao') return;

    console.info('[LogoutListener] TAO logout received from', event.origin);
    this.ngZone.run(() => this.performLogout());
  }

  private performLogout(): void {
    if (this.loggedOut) return;
    this.loggedOut = true;
    this.sessionStorage.clearEntireSession();
    window.close();
  }
}
